import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey, jadeGreen, lightBlue, volcano } from '../design-system/theme';
import { AdminShell } from '../components/admin/AdminShell';
import { useOnboardingTour, WelcomePrompt } from '../components/OnboardingTour';
import {
  PageHeader,
  PageTitle,
  Card,
  CardTitle,
  Section,
  SectionTitle,
  PrimaryButton,
} from '../styles/adminShared';
import { PrivacyLawsGuide, KeyConceptsGuide, ProcessGuides, InfographicViewer, PLATFORM_OVERVIEW_SYNTAX } from '../components/HelpCenter';

// ===== PAGE-SPECIFIC STYLED COMPONENTS =====
// Only components that are truly specific to this page


// Quick Links Grid
const QuickLinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QuickLinkCard = styled(Card)`
  padding: 24px;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const CardIcon = styled.div`
  font-size: 32px;
  margin-bottom: 16px;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${theme.textSecondary};
  line-height: 1.5;
`;

// FAQ Components
const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FAQItem = styled.div<{ $expanded: boolean }>`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.textPrimary};
  text-align: left;
  transition: background 0.2s;
  
  svg {
    flex-shrink: 0;
    transition: transform 0.2s;
  }
  
  &:hover {
    background: ${neutral[2]};
  }
`;

const FAQAnswer = styled.div<{ $expanded: boolean }>`
  max-height: ${props => props.$expanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const FAQAnswerContent = styled.div`
  padding: 0 24px 20px;
  font-size: 14px;
  color: ${theme.textSecondary};
  line-height: 1.7;
  
  a {
    color: ${theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  ul {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 4px;
  }
`;

// Keyboard Shortcuts Components
const KeyboardShortcutsCard = styled(Card)`
  padding: 24px;
`;

const ShortcutList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ShortcutItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${neutral[2]};
  border-radius: 8px;
`;

const ShortcutLabel = styled.span`
  font-size: 14px;
  color: ${theme.textPrimary};
`;

const ShortcutKeys = styled.div`
  display: flex;
  gap: 4px;
`;

const KeyBadge = styled.kbd`
  padding: 4px 10px;
  background: white;
  border: 1px solid ${neutral[4]};
  border-radius: 6px;
  font-family: 'SF Mono', monospace;
  font-size: 12px;
  color: ${neutral[7]};
  box-shadow: 0 2px 0 ${neutral[4]};
`;

// Contact Section Components
const ContactCardGradient = styled.div`
  background: ${theme.gradientPrimary};
  border-radius: 16px;
  padding: 32px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
`;

const ContactInfo = styled.div`
  h3 {
    margin: 0 0 8px 0;
    font-size: 22px;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
  }
`;

const ContactButtonLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: white;
  color: ${theme.primary};
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }
`;

// FAQ Data
const faqs = [
  {
    question: 'How do I create a new consent template?',
    answer: `To create a consent template:
    <ul>
      <li>Go to <strong>Templates</strong> from the sidebar</li>
      <li>Click <strong>Create Template</strong></li>
      <li>Fill in the template name, description, and consent text</li>
      <li>Select data categories and evidence types (signature, OTP, etc.)</li>
      <li>Configure branding and validity settings</li>
      <li>Save as draft or publish immediately</li>
    </ul>`,
  },
  {
    question: 'How do I send a consent form to data owners?',
    answer: `You can send consent forms in several ways:
    <ul>
      <li>Navigate to <strong>Generate URL</strong> in the sidebar</li>
      <li>Select a consent form from your templates</li>
      <li>Enter the data owner's details (email or phone)</li>
      <li>Choose delivery method: Email, SMS, or generate a shareable link</li>
      <li>The system will send the consent collection link automatically</li>
    </ul>`,
  },
  {
    question: 'What evidence types are supported?',
    answer: `Consent Platform supports multiple evidence types for compliance:
    <ul>
      <li><strong>Digital Signature</strong> - Capture drawn signatures on any device</li>
      <li><strong>Phone OTP</strong> - Verify identity via SMS one-time password</li>
      <li><strong>Email OTP</strong> - Verify identity via email one-time password</li>
      <li><strong>Video Recording</strong> - Record consent acknowledgment video</li>
    </ul>
    You can require multiple evidence types per consent for enhanced compliance.`,
  },
  {
    question: 'How do I handle withdrawal requests?',
    answer: `Withdrawal requests are managed through the <strong>Withdrawals</strong> section:
    <ul>
      <li>View all pending withdrawal requests</li>
      <li>Review the data owner's reason for withdrawal</li>
      <li>Approve or reject with documented reasoning</li>
      <li>Track data erasure status for GDPR compliance</li>
      <li>All actions are recorded in the audit log</li>
    </ul>`,
  },
  {
    question: 'How can I export consent artifacts for compliance?',
    answer: `To export consent artifacts:
    <ul>
      <li>Go to <strong>Consent Artifacts</strong></li>
      <li>Use filters to select specific records (date range, status, template)</li>
      <li>Click the <strong>Export</strong> button</li>
      <li>Choose format: CSV or PDF</li>
      <li>Artifacts include full audit trail, evidence references, and data erasure status</li>
    </ul>
    Exports are compliant with GDPR, DPDP Act, and Meity BRD requirements.`,
  },
  {
    question: 'How do I confirm data erasure after consent withdrawal?',
    answer: `After a consent is withdrawn or expires:
    <ul>
      <li>Navigate to the <strong>Data Erasure</strong> page from the sidebar</li>
      <li>Review consents flagged for data deletion</li>
      <li>Delete data from all systems (primary storage, backups, third parties)</li>
      <li>Click <strong>Confirm Erasure</strong> and add detailed notes</li>
      <li>System records the confirmation with timestamp for compliance</li>
    </ul>
    This process is required for DPDP Act and GDPR compliance.`,
  },
  {
    question: 'What is the difference between Projects and Data Fiduciaries?',
    answer: `<strong>Projects</strong> represent specific initiatives or campaigns that require consent collection (e.g., "Q1 Marketing Campaign", "Clinical Trial #123").
    
    <strong>Data Fiduciaries</strong> represent organizations or entities that collect and process data (e.g., hospitals, companies, research institutions).
    
    A single Data Fiduciary can have multiple Projects, and each Project can have multiple consent templates.`,
  },
];

// Quick Links
const quickLinks = [
  {
    icon: '📚',
    title: 'Documentation',
    description: 'Comprehensive guides and API reference',
    url: '/docs',
  },
  {
    icon: '🎥',
    title: 'Video Tutorials',
    description: 'Step-by-step video walkthroughs',
    url: '/tutorials',
  },
  {
    icon: '🔧',
    title: 'API Reference',
    description: 'REST API documentation for developers',
    url: '/api-docs',
  },
  {
    icon: '📋',
    title: 'Compliance Guides',
    description: 'GDPR, DPDP Act, and HIPAA guides',
    url: '/compliance',
  },
  {
    icon: '🔄',
    title: 'Changelog',
    description: 'Latest updates and release notes',
    url: '/changelog',
  },
  {
    icon: '💬',
    title: 'Community Forum',
    description: 'Connect with other users',
    url: '/community',
  },
];

// Keyboard shortcuts
const shortcuts = [
  { label: 'Go to Dashboard', keys: ['G', 'D'] },
  { label: 'Go to Templates', keys: ['G', 'T'] },
  { label: 'Go to Consents', keys: ['G', 'C'] },
  { label: 'Search', keys: ['/', 'or', '⌘', 'K'] },
  { label: 'Create New', keys: ['N'] },
  { label: 'Close Modal', keys: ['Esc'] },
];

// ===== NEW ENHANCED HELP CENTER =====

// Main Navigation Tabs
const MainTabContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 32px;
  background: ${neutral[2]};
  padding: 6px;
  border-radius: 16px;
  flex-wrap: wrap;
`;

const MainTab = styled.button<{ $active: boolean }>`
  padding: 14px 24px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  
  ${props => props.$active ? `
    background: white;
    color: ${theme.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  ` : `
    background: transparent;
    color: ${theme.textSecondary};
    
    &:hover {
      background: ${neutral[3]};
    }
  `}
`;

// Hero Section
const HeroSection = styled.div`
  background: linear-gradient(135deg, ${blueGrey[9]} 0%, ${blueGrey[7]} 100%);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 60%;
    height: 200%;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%);
    transform: rotate(-15deg);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  
  h1 {
    font-size: 32px;
    font-weight: 700;
    margin: 0 0 12px 0;
  }
  
  p {
    font-size: 16px;
    opacity: 0.9;
    margin: 0 0 24px 0;
    line-height: 1.6;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeroButton = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.$primary ? `
    background: white;
    color: ${blueGrey[9]};
    border: none;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
  ` : `
    background: transparent;
    color: white;
    border: 2px solid rgba(255,255,255,0.3);
    
    &:hover {
      background: rgba(255,255,255,0.1);
    }
  `}
`;

// Search Box
const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
  margin-bottom: 32px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px 16px 52px;
  border: 2px solid ${neutral[3]};
  border-radius: 14px;
  font-size: 15px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 4px rgba(54, 58, 80, 0.1);
  }
  
  &::placeholder {
    color: ${neutral[5]};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  color: ${neutral[5]};
`;

// Overview Cards Grid
const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const OverviewCard = styled.div<{ $color: string }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid ${neutral[3]};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.$color};
  }
  
  .icon-wrapper {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: ${props => props.$color}15;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    margin-bottom: 16px;
  }
  
  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${theme.textPrimary};
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: ${theme.textSecondary};
    line-height: 1.5;
  }
`;

// Help Center Tabs Type
type HelpTab = 'overview' | 'concepts' | 'processes' | 'privacy-laws' | 'faq';

export const HelpSupport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<HelpTab>('overview');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { showWelcome, startTour, skipTour, restartTour } = useOnboardingTour();

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  // Overview content with platform infographic
  const renderOverview = () => (
    <>
      <HeroSection>
        <HeroContent>
          <h1>👋 Welcome to Help Center</h1>
          <p>
            Everything you need to understand consent management, privacy laws, and how to use our platform effectively. 
            Start with the basics or dive deep into specific topics.
          </p>
          <HeroActions>
            <HeroButton $primary onClick={restartTour}>
              🎯 Take Product Tour
            </HeroButton>
            <HeroButton onClick={() => setActiveTab('processes')}>
              📚 Getting Started Guide
            </HeroButton>
          </HeroActions>
        </HeroContent>
      </HeroSection>

      <SearchContainer>
        <SearchIcon>🔍</SearchIcon>
        <SearchInput 
          placeholder="Search help topics, privacy laws, concepts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>

      <OverviewGrid>
        <OverviewCard $color={blueGrey[7]} onClick={() => setActiveTab('concepts')}>
          <div className="icon-wrapper">📚</div>
          <h3>Key Concepts</h3>
          <p>Understand tenants, fiduciaries, data owners, projects, templates, and more.</p>
        </OverviewCard>
        <OverviewCard $color={volcano[6]} onClick={() => setActiveTab('processes')}>
          <div className="icon-wrapper">📋</div>
          <h3>Process Guides</h3>
          <p>Step-by-step guides for consent collection, withdrawal handling, and getting started.</p>
        </OverviewCard>
        <OverviewCard $color={jadeGreen[6]} onClick={() => setActiveTab('privacy-laws')}>
          <div className="icon-wrapper">⚖️</div>
          <h3>Privacy Laws</h3>
          <p>Learn about DPDP, GDPR, CCPA, and other global privacy regulations.</p>
        </OverviewCard>
        <OverviewCard $color={lightBlue[6]} onClick={() => setActiveTab('faq')}>
          <div className="icon-wrapper">❓</div>
          <h3>FAQ & Support</h3>
          <p>Find answers to common questions and get help from our support team.</p>
        </OverviewCard>
      </OverviewGrid>

      {/* Platform Overview Infographic */}
      <Card style={{ padding: '32px', marginBottom: '24px' }}>
        <SectionTitle style={{ marginBottom: '24px' }}>🏗️ Platform Overview</SectionTitle>
        <InfographicViewer syntax={PLATFORM_OVERVIEW_SYNTAX} height={420} />
      </Card>

      {/* Quick Links */}
      <Section>
        <SectionTitle>🔗 Quick Links</SectionTitle>
        <QuickLinksGrid>
          {quickLinks.map((link, index) => (
            <QuickLinkCard key={index} onClick={() => {
              if (link.url === '/docs') setActiveTab('concepts');
              else if (link.url === '/compliance') setActiveTab('privacy-laws');
              else window.open(link.url, '_blank');
            }}>
              <CardIcon>{link.icon}</CardIcon>
              <CardTitle>{link.title}</CardTitle>
              <CardDescription>{link.description}</CardDescription>
            </QuickLinkCard>
          ))}
        </QuickLinksGrid>
      </Section>
    </>
  );

  // FAQ & Support content
  const renderFAQ = () => (
    <>
      {/* Restart Tour Button */}
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <PrimaryButton onClick={restartTour}>
            🎯 Take the Product Tour
          </PrimaryButton>
          <span style={{ color: theme.textSecondary, fontSize: '14px' }}>
            New here? Take a quick tour to learn the basics.
          </span>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <SectionTitle>❓ Frequently Asked Questions</SectionTitle>
        <FAQList>
          {faqs.map((faq, index) => (
            <FAQItem key={index} $expanded={expandedFAQ === index}>
              <FAQQuestion onClick={() => toggleFAQ(index)}>
                {faq.question}
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{ transform: expandedFAQ === index ? 'rotate(180deg)' : 'rotate(0)' }}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === index}>
                <FAQAnswerContent dangerouslySetInnerHTML={{ __html: faq.answer }} />
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQList>
      </Section>

      {/* Keyboard Shortcuts */}
      <Section>
        <SectionTitle>⌨️ Keyboard Shortcuts</SectionTitle>
        <KeyboardShortcutsCard>
          <ShortcutList>
            {shortcuts.map((shortcut, index) => (
              <ShortcutItem key={index}>
                <ShortcutLabel>{shortcut.label}</ShortcutLabel>
                <ShortcutKeys>
                  {shortcut.keys.map((key, keyIndex) => (
                    key === 'or' ? (
                      <span key={keyIndex} style={{ color: theme.textSecondary, fontSize: '12px', padding: '0 4px' }}>or</span>
                    ) : (
                      <KeyBadge key={keyIndex}>{key}</KeyBadge>
                    )
                  ))}
                </ShortcutKeys>
              </ShortcutItem>
            ))}
          </ShortcutList>
        </KeyboardShortcutsCard>
      </Section>

      {/* Contact Support */}
      <Section>
        <ContactCardGradient>
          <ContactInfo>
            <h3>Need more help?</h3>
            <p>Our support team is available 24/7 to assist you</p>
          </ContactInfo>
          <ContactButtonLink href="mailto:support@tashi.io">
            📧 Contact Support
          </ContactButtonLink>
        </ContactCardGradient>
      </Section>
    </>
  );

  return (
    <AdminShell title="Help Center">
      {showWelcome && (
        <WelcomePrompt onStartTour={startTour} onSkip={skipTour} />
      )}
      
      <PageHeader>
        <PageTitle>📖 Help Center</PageTitle>
      </PageHeader>

      {/* Main Tab Navigation */}
      <MainTabContainer>
        <MainTab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          🏠 Overview
        </MainTab>
        <MainTab $active={activeTab === 'concepts'} onClick={() => setActiveTab('concepts')}>
          📚 Key Concepts
        </MainTab>
        <MainTab $active={activeTab === 'processes'} onClick={() => setActiveTab('processes')}>
          📋 Process Guides
        </MainTab>
        <MainTab $active={activeTab === 'privacy-laws'} onClick={() => setActiveTab('privacy-laws')}>
          ⚖️ Privacy Laws
        </MainTab>
        <MainTab $active={activeTab === 'faq'} onClick={() => setActiveTab('faq')}>
          ❓ FAQ & Support
        </MainTab>
      </MainTabContainer>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'concepts' && <KeyConceptsGuide />}
      {activeTab === 'processes' && <ProcessGuides />}
      {activeTab === 'privacy-laws' && <PrivacyLawsGuide />}
      {activeTab === 'faq' && renderFAQ()}
    </AdminShell>
  );
};

export default HelpSupport;
